import React, { useEffect, useMemo } from 'react';
import { Gallery } from '../Gallery';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { PageSection } from '../PageSection';
import { useCardTelemetry, TelemetryProvider, enrichCardTelemetryExtraInfo } from '@apps/card/Card.telemetry';
import { useDesignTemplates } from './useDesignTemplates';
import { useGalleryItemsWithValueProps } from './useGalleryItemsWithValueProps';
import { CustomizeNowClickOverrideHandler, StationeryTemplate } from './DesignsGallery.types';
import { useUrlSyncedFilters } from './useUrlSyncedFilters';
import { GalleryItem, GalleryTemplate } from './components/GalleryItem/GalleryItem';
import { useConciergeDisclosure } from './useConciergeDisclosure';
import { BackToTop } from '@shared/components/BackToTop';
import { FilterFacet } from './components/Filters/Filter.types';
import { DesignGalleryFiltersProvider } from './DesignGalleryFiltersProvider';
import { useApplySearchFiltersToTemplates } from './useApplySearchFiltersToTemplates';
import { SearchAndFiltersContainer } from './components/Filters/SearchAndFilterContainer';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useStationeryPagination } from './useStationeryPagination';
import { GalleryPagination } from './components/GalleryPagination';
import { GallerySeo } from './DesignsGallerySeo';
import { CardFormat } from '../ProductDetailsPage/constants';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import { ItemImpressionsTelemetryProvider } from './components/ItemImpressionsTelemetry';
import { FaqSection } from '@apps/card/routes/PublicShop/components/FaqSection';
import { useStationeryFaqItems } from '@apps/card/routes/PublicShop/components/FaqSection/useStationeryFaqItems';
import { JumbotronSection } from './components/JumbotronSection';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { SeoContentSection } from '@apps/card/routes/PublicShop/components/SeoContentSection';
import { RelatedLinks } from '@apps/card/routes/PublicShop/components/RelatedLinks';
import { SeoReviewsSection } from '@apps/card/routes/PublicShop/components/SeoReviewsSection';
import { BreadcrumbSection } from './components/BreadcrumbSection';
import { useTagFilteredTemplates } from './useTagFilteredTemplates';
import { useFilterFacets } from './useFilterFacets';
import { useProducts } from './useProducts';
import { DesignsGallerySkeleton } from './DesignsGallerySkeleton';
import { useFuse } from './components/Search/useFuse';
import { NoResultsFound } from './NoResultsFound';
import { useSearchFilteredGalleryItems } from './components/Search/useSearchFilteredGalleryItems';

interface DesignsGalleryProps {
  eventId?: string;
  eventHandle?: string;
  // Intended only to be used for the investor demo.
  onCustomizeCardClickOverride?: CustomizeNowClickOverrideHandler;
}

const Root = ({
  filterFacets,
  eventId, // Only used for FavoriteBadge
  onCustomizeCardClickOverride,
  templates,
}: {
  filterFacets: FilterFacet[];
  templates: ReadonlyArray<StationeryTemplate>;
} & DesignsGalleryProps) => {
  useUrlSyncedFilters();
  useCardTelemetry({ page: 'homeRoute' });
  const { currentCategory } = useCardCategoryContext();
  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });
  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);
  const { useSearchResults, searchError } = useApplySearchFiltersToTemplates();
  const { searchCompleted } = useSearchResults();
  if (searchCompleted && templates.length === 0) {
    searchError();
  }

  const tagFilteredTemplates = useTagFilteredTemplates(templates);
  const fuse = useFuse(templates);
  const tagFilteredItems: ReadonlyArray<GalleryTemplate> = useMemo(
    () =>
      tagFilteredTemplates.map(template => ({
        ...template,
        type: 'template',
      })),
    [tagFilteredTemplates],
  );
  const { isActive: isSearchActive, filteredItems } = useSearchFilteredGalleryItems(tagFilteredItems, fuse);
  const { itemsWithProps, galleryColumnCount } = useGalleryItemsWithValueProps(filteredItems, isSearchActive);
  const { items: paginatedTemplates, ...pagination } = useStationeryPagination(itemsWithProps, galleryColumnCount, isSearchActive);

  const { ConciergeDialog, onCardSelect } = useConciergeDisclosure(onCustomizeCardClickOverride);

  const params = useQueryParamHelper();
  const preferredFormat = (params.getValue('format') as CardFormat | undefined) ?? CardFormat.paper;

  return (
    <>
      {ConciergeDialog}
      <PageSection
        id="designs-gallery"
        paddingX={0}
        titleMarginX={{ _: 6, md: 8 }}
      >
        <SearchAndFiltersContainer filterFacets={filterFacets} />
        {(() => {
          if (isSearchActive && !filteredItems.length) return <NoResultsFound />;

          return (
            <>
              <Gallery>
                {paginatedTemplates.map((data, index) => {
                  return (
                    <GalleryItem
                      preferredFormat={preferredFormat}
                      cardTileSource="shop"
                      eventId={eventId}
                      onCardSelect={onCardSelect}
                      key={data.type === 'template' ? data.id : index}
                      data={data}
                      currentCategory={currentCategory}
                    />
                  );
                })}
              </Gallery>
              <GalleryPagination
                currentPage={pagination.page}
                pageCount={pagination.pages}
                onPageChange={pagination.setPage}
              />
            </>
          );
        })()}
      </PageSection>
    </>
  );
};

export const DesignsGallery = ({ eventId, eventHandle, onCustomizeCardClickOverride }: DesignsGalleryProps) => {
  const { currentCategory } = useCardCategoryContext();
  const templates = useDesignTemplates({ eventId, eventHandle });
  const filterFacets = useFilterFacets(templates);

  const { isPublicShopRoute } = useCardsRouterContext();
  const products = useProducts();
  useFreeShippingBanner(products);
  const faqItems = useStationeryFaqItems(currentCategory);

  return (
    <>
      <GallerySeo
        currentCategory={currentCategory}
        faqItems={faqItems}
        isPublicShopRoute={isPublicShopRoute}
      />
      {isPublicShopRoute && <JumbotronSection category={currentCategory} />}
      {isPublicShopRoute && <BreadcrumbSection category={currentCategory} />}
      <SkeletonGroup
        isReady={!!templates.length}
        placeholder={<DesignsGallerySkeleton />}
      >
        <TelemetryProvider>
          <ItemImpressionsTelemetryProvider>
            <DesignGalleryFiltersProvider>
              <Root
                templates={templates}
                eventId={eventId}
                eventHandle={eventHandle}
                onCustomizeCardClickOverride={onCustomizeCardClickOverride}
                filterFacets={filterFacets}
              />
              <BackToTop />
            </DesignGalleryFiltersProvider>
          </ItemImpressionsTelemetryProvider>
        </TelemetryProvider>
      </SkeletonGroup>
      {isPublicShopRoute && <RelatedLinks category={currentCategory} />}
      <FaqSection faqItems={faqItems} />
      <SeoReviewsSection category={currentCategory} />
      <SeoContentSection category={currentCategory} />
    </>
  );
};
